import React, { useState, useEffect, useCallback, useRef } from 'react';
import { decryptApiKey } from './utils/encryption';

// Import components
import UnifiedSidebar from './components/UnifiedSidebar';
import Dashboard from './components/Dashboard';
import ClassWorkspace from './components/ClassWorkspace';
import ClassManagement from './components/Admin/ClassManagement';
// import DocumentEditor from './components/DocumentEditor'; // Used in ClassWorkspace
import LandingPage from './components/LandingPage';
import PublicClassView from './components/PublicClassView';
import UpdatePassword from './components/UpdatePassword';
import ApiKeyForm from './components/ApiKeyForm'
import ConsentForm from './components/ConsentForm';
import About from './components/About';
import UserSettings from './components/UserSettings';
import { ClassProvider, useClass } from './utils/ClassContext';
import { hasInstructorPermissions, canManageClasses } from './utils/permissions';

// Import styles
import './App.css';
import './styles/sidebar.css';

// API base URL - point to Flask backend
const API_BASE_URL = '/api';

// Internal component that has access to ClassContext
function AppContent() {
  // Access class context at the top of component
  const {
    shouldRedirectToDashboard,
    selectedClass,
    clearClass
  } = useClass();

  // Authentication state
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState({ firstName: '', lastName: '' });
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isViewingAsStudent, setIsViewingAsStudent] = useState(false);
  const [requiresPasswordUpdate, setRequiresPasswordUpdate] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  
  // Public class view state
  const [isPublicView, setIsPublicView] = useState(false);
  const [publicClassId, setPublicClassId] = useState(null);

  // Navigation state - will be set based on user role after authentication
  const [selectedPage, setSelectedPage] = useState('dashboard');

  // Navisgation data
  const [navigationData, setNavigationData] = useState(null);

  // User stats
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [quotaValue, setQuotaValue] = useState(10);
  const [notificationCount, setNotificationCount] = useState(0);
  const notificationRefreshRef = useRef(null);

  // Track if we're in the middle of class selection to prevent clearing
  const isSelectingClassRef = useRef(false);
  
  // SSE connection for real-time notifications
  const [sseConnection, setSseConnection] = useState(null);
  
  // Optimistic notification count updates
  const decrementNotificationCount = useCallback((amount = 1) => {
    setNotificationCount(prev => Math.max(0, prev - amount));
  }, []);

  const rollbackNotificationCount = useCallback((amount = 1) => {
    setNotificationCount(prev => prev + amount);
  }, []);

  // Unified sidebar collapsed state - always start expanded on page load
  const [unifiedSidebarCollapsed, setUnifiedSidebarCollapsed] = useState(false);

  // Reset sidebar to expanded on page load
  useEffect(() => {
    setUnifiedSidebarCollapsed(false);
    localStorage.setItem('unifiedSidebarCollapsed', 'false');
  }, []);

  // Redirect to dashboard if on a class page but no class is selected
  // This happens on page refresh since selectedClass is not persisted
  useEffect(() => {
    // Don't interfere if we're in the middle of selecting a class
    if (isSelectingClassRef.current) {
      return;
    }

    // If no class is selected but we're on a class-specific page, redirect to dashboard
    if (!selectedClass && selectedPage !== 'dashboard') {
      const globalPages = ['settings', 'help', 'about', 'api-key-form', 'consent', 'class-management'];
      if (!globalPages.includes(selectedPage)) {
        console.log('[App] Redirecting to dashboard - no class selected on class page');
        setSelectedPage('dashboard');
        localStorage.setItem('selectedPage', 'dashboard');
      }
    }
  }, [selectedClass, selectedPage]); // Run when class or page changes

  // Clear work state for previous user when logging in as different user
  const clearPreviousUserState = (newUsername) => {
    const currentSavedUsername = localStorage.getItem('username');

    if (currentSavedUsername && currentSavedUsername !== newUsername) {
      // Clear the previous user's work state
      localStorage.removeItem(`erd_work_state_${currentSavedUsername}`);
      localStorage.removeItem(`erd_notation_${currentSavedUsername}`);
      localStorage.removeItem(`textarea_height_${currentSavedUsername}`);
      localStorage.removeItem(`panel_width_${currentSavedUsername}`);
    }
  };

  // Fetch user statistics from API with useCallback - SINGLE VERSION
  const fetchUserStats = useCallback(async (user, roleOverride = null) => {
    try {
      const role = roleOverride || userRole;

      if (!role) {
        console.warn('No user role available for API request');
        setLoading(false);
        return;
      }

      // Build URL with optional class_id parameter for class-scoped stats
      let url = `${API_BASE_URL}/user-stats/${user}`;

      // Add class_id if we have a selected class for class-scoped stats
      if (selectedClass && selectedClass.class_id) {
        url += `?class_id=${encodeURIComponent(selectedClass.class_id)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': role,
          'Requesting-User': user,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFeedbackCount(data.feedbackCount || 0);
        setQuotaValue(data.quotaValue || 10);
        // Note: notificationCount now comes from SSE, not from this API call
        setHasConsent(data.hasConsent || false);
        // Update API key based on backend response (decrypt and do not cache in localStorage)
        if (data.apiKey) {
          const decryptedApiKey = decryptApiKey(data.apiKey);
          setApiKey(decryptedApiKey);
        } else {
          // Clear API key if backend doesn't provide one (user may have lost access)
          setApiKey('');
        }
        // Note: API keys are not cached in localStorage and are fetched fresh each time
      } else {
        console.error('Failed to fetch user stats');
        setFeedbackCount(0);
        setQuotaValue(10);
        // Note: notificationCount now comes from SSE
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setFeedbackCount(0);
      setQuotaValue(10);
      // Note: notificationCount now comes from SSE
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  // Setup SSE connection for real-time notifications
  const setupNotificationSSE = useCallback((user) => {
    if (!user) return;
    
    // Close existing connection
    if (sseConnection) {
      console.log('Closing existing SSE connection');
      sseConnection.close();
    }
    
    console.log(`Setting up SSE connection for notifications: ${user}`);
    
    const eventSource = new EventSource(`${API_BASE_URL}/notifications/stream/${user}`, {
      withCredentials: true
    });
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Only log non-heartbeat messages or when count changes
        if (data.type !== 'heartbeat' || (data.count !== notificationCount)) {
          if (data.type !== 'heartbeat') {
            console.log('SSE notification update:', data);
          }
        }

        if (data.type === 'initial_count' || data.type === 'count_update' || data.type === 'heartbeat') {
          // Only log count updates when they actually change
          if (data.count !== notificationCount) {
            console.log(`Setting notification count from SSE: ${data.count}`);
          }
          setNotificationCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error parsing SSE notification data:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.log('SSE connection error:', error);
      // EventSource will automatically attempt to reconnect
    };
    
    eventSource.onopen = () => {
      console.log('SSE connection opened for notifications');
    };
    
    // Log connection status after a delay - only if there are issues
    setTimeout(() => {
      if (eventSource.readyState !== 1) { // 1 = OPEN
        console.log('SSE connection state:', eventSource.readyState);
      }
    }, 5000);
    
    setSseConnection(eventSource);
    return eventSource;
  }, []); // Remove sseConnection from dependencies to prevent loops

  // Function to fetch class-specific notification count
  const fetchClassNotificationCount = useCallback(async (user, classId) => {
    if (!user || !classId) return 0;
    
    try {
      const response = await fetch(`${API_BASE_URL}/notifications/count/${user}/${classId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.count || 0;
      } else {
        console.error('Failed to fetch class notification count');
        return 0;
      }
    } catch (error) {
      console.error('Error fetching class notification count:', error);
      return 0;
    }
  }, []);

  // Check for public class URL on component mount
  useEffect(() => {
    const path = window.location.pathname;
    const publicClassMatch = path.match(/^\/public\/classes\/([^/]+)/);
    
    if (publicClassMatch) {
      const classId = publicClassMatch[1];
      setIsPublicView(true);
      setPublicClassId(classId);
      setUserRole('Public');
      setAuthenticated(false); // Not authenticated, but viewing public class
      setLoading(false);
      return; // Don't proceed with authentication check
    } else {
      setIsPublicView(false);
      setPublicClassId(null);
    }
  }, []);

  // Check authentication on component mount
  useEffect(() => {
    // Skip authentication check if we're in public view
    if (isPublicView) {
      return;
    }

    const verifyAuthentication = async () => {
      try {
        // Always verify with the server — handles both password and SSO logins.
        // SSO never sets localStorage, so we cannot gate on it.
        const response = await fetch(`${API_BASE_URL}/verify`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.authenticated) {
            const serverUser = data.user;

            // Sync localStorage so subsequent page loads work normally
            const nameObj = { firstName: serverUser.firstname || '', lastName: serverUser.lastname || '' };
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('username', serverUser.username);
            localStorage.setItem('userRole', serverUser.role);
            localStorage.setItem('userName', JSON.stringify(nameObj));

            setAuthenticated(true);
            setUsername(serverUser.username);
            setUserRole(serverUser.role);
            setUserName(nameObj);

            const defaultPage = canManageClasses(serverUser.role) ? 'class-management' : 'dashboard';
            setSelectedPage(defaultPage);
            localStorage.setItem('selectedPage', defaultPage);

            await fetchUserStats(serverUser.username, serverUser.role);
          } else {
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('username');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            setLoading(false);
          }
        } else {
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('username');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userName');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error verifying authentication:', error);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('username');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userName');
        setLoading(false);
      }
    };

    verifyAuthentication();
  }, [fetchUserStats, setupNotificationSSE, isPublicView]);

  // Cleanup SSE connection on component unmount
  useEffect(() => {
    return () => {
      if (sseConnection) {
        console.log('Cleaning up SSE connection on unmount');
        sseConnection.close();
      }
    };
  }, []); // Only run on mount/unmount

  // Setup SSE connection when user is authenticated
  useEffect(() => {
    if (authenticated && username && !sseConnection) {
      console.log('Setting up SSE for authenticated user:', username);
      setupNotificationSSE(username);
    }
  }, [authenticated, username, setupNotificationSSE, sseConnection]);

  // Debug notification count changes - only log when count is non-zero
  useEffect(() => {
    if (notificationCount > 0) {
      console.log('Notification count updated to:', notificationCount);
    }
  }, [notificationCount]);

  // Auto-redirect based on user role when role is set
  useEffect(() => {
    if (userRole && authenticated) {
      const defaultPage = canManageClasses(userRole) ? 'class-management' : 'dashboard';
      setSelectedPage(defaultPage);
      localStorage.setItem('selectedPage', defaultPage);
    }
  }, [userRole, authenticated]);

  // Clear navigation data when leaving discussions page
  useEffect(() => {
    if (selectedPage !== 'discussions') {
      setNavigationData(null);
    }
  }, [selectedPage]);

  // Listen for unified sidebar state changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('unifiedSidebarCollapsed');
      setUnifiedSidebarCollapsed(saved !== null ? JSON.parse(saved) : false);
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for direct changes within the same tab
    const interval = setInterval(() => {
      const saved = localStorage.getItem('unifiedSidebarCollapsed');
      const currentState = saved !== null ? JSON.parse(saved) : false;
      if (currentState !== unifiedSidebarCollapsed) {
        setUnifiedSidebarCollapsed(currentState);
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [unifiedSidebarCollapsed]);

  // Handle successful login
  const handleLogin = async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if password update is required
        if (data.password_update_required) {
          setUsername(data.user.username);
          setUserRole(data.user.role);
          setUserName({ firstName: data.user.firstname, lastName: data.user.lastname });
          setRequiresPasswordUpdate(true);
          return true;
        }

        if (data.success) {
          // Clear previous user's work state before setting new user
          clearPreviousUserState(data.user.username);

          // Set authentication state
          setAuthenticated(true);
          setUsername(data.user.username);
          setUserRole(data.user.role);
          setUserName({ firstName: data.user.firstname, lastName: data.user.lastname });
          setApiKey(data.apiKey || '');

          // Store in localStorage for persistence
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('username', data.user.username);
          localStorage.setItem('userRole', data.user.role);
          localStorage.setItem('userName', JSON.stringify({ firstName: data.user.firstname || '', lastName: data.user.lastname || '' }));
          localStorage.setItem('apiKey', data.apiKey || '');

          // Fetch user stats with the role from login response
          await fetchUserStats(data.user.username, data.user.role);

          // SSE will be setup after authentication verification

          // Set default page based on user role
          const defaultPage = canManageClasses(data.user.role) ? 'class-management' : 'dashboard';
          setSelectedPage(defaultPage);
          localStorage.setItem('selectedPage', defaultPage);

          return true;
        }
      }

      throw new Error(data.error || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Handle password update completion
  const handlePasswordUpdated = () => {
    // Reset states and redirect to login
    setRequiresPasswordUpdate(false);
    setUsername('');
    alert('Password updated successfully. Please log in with your new password.');
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state regardless of API call success
      setAuthenticated(false);
      setUsername('');
      setUserRole('');
      setUserName({ firstName: '', lastName: '' });
      setApiKey(null);
      setSelectedPage('home');
      setFeedbackCount(0);
      setQuotaValue(10);
      setNotificationCount(0);
      setRequiresPasswordUpdate(false);

      // Close SSE connection
      if (sseConnection) {
        sseConnection.close();
        setSseConnection(null);
      }

      // Clear localStorage including page state
      localStorage.clear();
    }
  };

  // View as student — swap to teststudent credentials without logging out
  const STUDENT_VIEW_USERNAME = 'teststudent';
  const STUDENT_VIEW_ROLE = 'Student';

  const handleViewAsStudent = () => {
    // Stash real credentials
    localStorage.setItem('_real_username', username);
    localStorage.setItem('_real_userRole', userRole);
    // Switch to student identity
    localStorage.setItem('username', STUDENT_VIEW_USERNAME);
    localStorage.setItem('userRole', STUDENT_VIEW_ROLE);
    setUsername(STUDENT_VIEW_USERNAME);
    setUserRole(STUDENT_VIEW_ROLE);
    setUserName({ firstName: 'Test', lastName: 'Student' });
    setIsViewingAsStudent(true);
    setSelectedPage('dashboard');
  };

  const handleExitStudentView = () => {
    const realUsername = localStorage.getItem('_real_username') || '';
    const realRole = localStorage.getItem('_real_userRole') || 'Instructor';
    localStorage.setItem('username', realUsername);
    localStorage.setItem('userRole', realRole);
    localStorage.removeItem('_real_username');
    localStorage.removeItem('_real_userRole');
    setUsername(realUsername);
    setUserRole(realRole);
    setUserName({ firstName: '', lastName: '' });
    setIsViewingAsStudent(false);
    setSelectedPage('dashboard');
  };

  // Update API key
  const updateApiKey = async (newApiKey) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username,
        },
        body: JSON.stringify({
          username: username,
          apiKey: newApiKey
        }),
      });

      if (response.ok) {
        setApiKey(newApiKey);
        localStorage.setItem('apiKey', newApiKey);
        return true;
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update API key');
      }
    } catch (error) {
      console.error('Error updating API key:', error);
      throw error;
    }
  };

  // Handle API key submission
  const handleApiKeySubmit = async (apiKey) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username,
        },
        body: JSON.stringify({
          username: username,
          apiKey: apiKey
        }),
      });

      if (response.ok) {
        // Update the user's API key state
        setApiKey(apiKey);
        localStorage.setItem('apiKey', apiKey);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.message || 'Invalid API key' };
      }
    } catch (error) {
      console.error('Error submitting API key:', error);
      return { success: false, error: 'Network error occurred' };
    }
  };

  // Note: Notification count is now managed entirely by SSE

  // Refresh user stats
  const refreshUserStats = useCallback(() => {
    if (username) {
      // If we have a selected class, fetch class-scoped stats
      if (selectedClass && selectedClass.class_id) {
        const fetchClassStats = async () => {
          try {
            if (!userRole) return;

            const url = `${API_BASE_URL}/user-stats/${username}?class_id=${encodeURIComponent(selectedClass.class_id)}`;
            console.log('[App] Refreshing class-scoped stats:', url);

            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'User-Role': userRole,
                'Requesting-User': username,
              },
            });

            if (response.ok) {
              const data = await response.json();
              setFeedbackCount(data.feedbackCount || 0);
              setQuotaValue(data.quotaValue || 10);
              setHasConsent(data.hasConsent || false);
              if (data.apiKey) {
                const decryptedApiKey = decryptApiKey(data.apiKey);
                setApiKey(decryptedApiKey);
              } else {
                setApiKey('');
              }
            }
          } catch (error) {
            console.error('Error refreshing class stats:', error);
          }
        };

        fetchClassStats();
      } else {
        // No class selected, use the original fetchUserStats
        fetchUserStats(username);
      }
    }
  }, [username, selectedClass, userRole, fetchUserStats]);

  // Function that triggers all UI updates
  const triggerAllUIUpdates = useCallback(() => {
    if (username) {
      fetchUserStats(username);

      if (notificationRefreshRef.current) {
        notificationRefreshRef.current();
      }
    }
  }, [username, fetchUserStats]);

  // Refresh user stats when selected class changes
  useEffect(() => {
    if (username && selectedClass && selectedClass.class_id) {
      console.log('[App] Selected class changed, refreshing user stats for class:', selectedClass.class_id);

      // Fetch stats directly with current selectedClass to avoid stale closure
      const fetchClassStats = async () => {
        try {
          if (!userRole) {
            console.warn('No user role available for API request');
            return;
          }

          const url = `${API_BASE_URL}/user-stats/${username}?class_id=${encodeURIComponent(selectedClass.class_id)}`;
          console.log('[App] Fetching class-scoped stats:', url);

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'User-Role': userRole,
              'Requesting-User': username,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setFeedbackCount(data.feedbackCount || 0);
            setQuotaValue(data.quotaValue || 10);
            setHasConsent(data.hasConsent || false);
            if (data.apiKey) {
              const decryptedApiKey = decryptApiKey(data.apiKey);
              setApiKey(decryptedApiKey);
            } else {
              setApiKey('');
            }
          }
        } catch (error) {
          console.error('Error fetching class stats:', error);
        }
      };

      fetchClassStats();
    }
  }, [selectedClass, username, userRole]); // Dependencies that trigger refresh

  // Navigation handler with guards
  const handleNavigation = (pageId, data = null) => {
    console.log('[App] handleNavigation called', {
      pageId: pageId,
      data: data,
      currentPage: selectedPage,
      hasData: !!data
    });

    // Skip redirect check when navigating from dashboard to home
    // This is the expected flow after class selection, and the context may not have updated yet
    const isClassSelectionFlow = selectedPage === 'dashboard' && pageId === 'home';

    // Mark that we're in a class selection flow and update localStorage BEFORE navigation
    if (isClassSelectionFlow) {
      console.log('[App] ✅ Class selection flow detected');
      // Flag should already be set by Dashboard, but set as backup if not
      if (!isSelectingClassRef.current) {
        console.log('[App] ⚠️ Flag not set by Dashboard, setting as backup');
        isSelectingClassRef.current = true;
        // Clear the flag after navigation completes - Dashboard will also clear it
        setTimeout(() => {
          console.log('[App] ⏰ Clearing class selection protection flag (backup)');
          isSelectingClassRef.current = false;
        }, 500);
      } else {
        console.log('[App] 🛡️ Protection flag already set by Dashboard');
      }
      // Update localStorage immediately to prevent sync logic from clearing class
      localStorage.setItem('selectedPage', 'home');
    }

    // Check if user should be redirected to dashboard
    if (!isClassSelectionFlow && shouldRedirectToDashboard(pageId)) {
      console.log('[App] Redirecting to dashboard - no class selected');
      setSelectedPage('dashboard');
      localStorage.setItem('selectedPage', 'dashboard');
      setNavigationData(null);
      return;
    }

    setSelectedPage(pageId);
    // Don't update localStorage again if we already did it for class selection
    if (!isClassSelectionFlow) {
      localStorage.setItem('selectedPage', pageId);
    }

    // Store navigation data if provided
    if (data) {
      console.log('[App] Setting navigation data:', data);
      setNavigationData(data);
    } else {
      // Clear navigation data if no data provided (normal navigation)
      console.log('[App] Clearing navigation data');
      setNavigationData(null);
    }

    console.log('[App] Navigation completed', { newPage: pageId });
  };

  // Handle consent form submission
  const handleConsentSubmitted = async () => {
    try {
      console.log('Consent submitted, updating user profile...');

      // First, update the user stats
      await fetchUserStats(username);

      // Now, get the user's updated profile including the admin API key
      const profileResponse = await fetch(`${API_BASE_URL}/user-profile/${username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username,
        },
        credentials: 'include',
      });

      if (profileResponse.ok) {
        const profileResponse_data = await profileResponse.json();
        const profileData = profileResponse_data.user; // Extract user data from response

        // Update all relevant states
        setHasConsent(true);

        // Update API key with the admin key the user now has access to (decrypt and do not cache)
        if (profileData.apiKey) {
          const decryptedApiKey = decryptApiKey(profileData.apiKey);
          setApiKey(decryptedApiKey);
          console.log('API key updated after consent submission');
        }

        // Update other profile data if needed
        if (profileData.role) setUserRole(profileData.role);
        if (profileData.firstname && profileData.lastname) {
          setUserName({ firstName: profileData.firstname, lastName: profileData.lastname });
        }

      } else {
        console.error('Failed to fetch updated user profile');
        // Still set consent to true since the form was submitted successfully
        setHasConsent(true);
      }

      // Navigate to home
      setSelectedPage('home');

      alert('Consent form submitted successfully! You now have access to AI feedback.');

    } catch (error) {
      console.error('Error after consent submission:', error);
      // Even if there's an error, set consent to true since the form was submitted
      setHasConsent(true);
      alert('Consent form submitted successfully! Please refresh the page if you don\'t see updated access.');
    }
  };

  // Handle class selection from dashboard - sidebar stays visible but switches to class mode
  const handleClassSelect = () => {
    // Unified sidebar will automatically switch to class mode when selectedClass is set
    // No need to collapse
  };

  // Render the current page based on selectedPage state
  const renderCurrentPage = () => {
    // Global pages that don't require class context
    switch (selectedPage) {
      case 'dashboard':
        return (
          <Dashboard
            username={username}
            userRole={userRole}
            apiBaseUrl={API_BASE_URL}
            onNavigate={handleNavigation}
            userName={userName}
            onClassSelect={handleClassSelect}
            isSelectingClassRef={isSelectingClassRef}
          />
        );

      case 'class-management':
        // Only allow admins to access class management
        if (!canManageClasses(userRole)) {
          return (
            <div className="p-8">
              <h1 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h1>
              <p className="text-gray-600">You don't have permission to access class management.</p>
            </div>
          );
        }
        return (
          <ClassManagement
            username={username}
            userRole={userRole}
            apiBaseUrl={API_BASE_URL}
            onNavigate={handleNavigation}
          />
        );


      case 'api-key-form':
        return (
          <ApiKeyForm
            username={username}
            onNavigate={handleNavigation}
            onApiKeySubmit={handleApiKeySubmit}
          />
        );

      case 'consent':
        return (
          <ConsentForm
            username={username}
            apiBaseUrl={API_BASE_URL}
            onConsentSubmitted={handleConsentSubmitted}
          />
        );

      case 'settings':
        return (
          <UserSettings
            username={username}
            userRole={userRole}
            apiBaseUrl={API_BASE_URL}
          />
        );

      case 'help':
        return (
          <div className="p-8">
            <h1 className="text-2xl font-bold mb-4">Help & Support</h1>
            <p className="text-gray-600">Help documentation coming soon...</p>
          </div>
        );

      case 'about':
        return <About />;

      default:
        // All other pages are class-specific and handled by ClassWorkspace
        return (
          <ClassWorkspace
            username={username}
            userRole={userRole}
            userName={userName}
            apiKey={apiKey}
            feedbackCount={feedbackCount}
            quotaValue={quotaValue}
            notificationCount={notificationCount}
            hasConsent={hasConsent}
            updateApiKey={updateApiKey}
            refreshUserStats={refreshUserStats}
            onNavigate={handleNavigation}
            apiBaseUrl={API_BASE_URL}
            hasInstructorPermissions={hasInstructorPermissions}
            navigationData={navigationData}
            notificationRefreshRef={notificationRefreshRef}
            triggerAllUIUpdates={triggerAllUIUpdates}
            onConsentSubmitted={handleConsentSubmitted}
            fetchClassNotificationCount={fetchClassNotificationCount}
            decrementNotificationCount={decrementNotificationCount}
            rollbackNotificationCount={rollbackNotificationCount}
            currentPage={selectedPage}
          />
        );
    }
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show password update form if required
  if (requiresPasswordUpdate) {
    return (
      <UpdatePassword
        username={username}
        onPasswordUpdated={handlePasswordUpdated}
      />
    );
  }

  // If in public view mode, show public class view
  if (isPublicView && publicClassId) {
    return (
      <PublicClassView
        classId={publicClassId}
        apiBaseUrl={API_BASE_URL}
        onLogin={handleLogin}
      />
    );
  }

  // If not authenticated, show landing page with public classes and login
  if (!authenticated) {
    return <LandingPage onLogin={handleLogin} apiBaseUrl={API_BASE_URL} />;
  }
  
  // Determine content margin based on sidebar state
  const getContentMargin = () => {
    if (unifiedSidebarCollapsed) {
      return 'ml-16'; // 64px
    } else {
      return 'ml-64'; // 256px
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 font-sans">
      {/* Unified Sidebar - Always visible */}
      <div className="fixed left-0 top-0 h-full z-30">
        <UnifiedSidebar
          username={username}
          userRole={isViewingAsStudent ? userRole : (selectedClass?.user_role || userRole)}
          userName={userName}
          selectedPage={selectedPage}
          feedbackCount={feedbackCount}
          quotaValue={quotaValue}
          notificationCount={notificationCount}
          onNavigate={handleNavigation}
          refreshUserStats={refreshUserStats}
          onLogout={handleLogout}
          isPublicView={false}
          isViewingAsStudent={isViewingAsStudent}
          onViewAsStudent={handleViewAsStudent}
          onExitStudentView={handleExitStudentView}
        />
      </div>

      {/* View-as-student banner */}
      {isViewingAsStudent && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-sm font-medium flex items-center justify-between px-4 py-1.5 shadow-md">
          <span>👁 Viewing as student (<strong>teststudent</strong>) — this is how students see the system</span>
          <button
            onClick={handleExitStudentView}
            className="ml-4 px-3 py-0.5 bg-white text-amber-700 rounded font-semibold hover:bg-amber-100 transition-colors"
          >
            Exit Student View
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`${getContentMargin()} flex-1 flex flex-col overflow-hidden transition-all duration-300 ${isViewingAsStudent ? 'pt-8' : ''}`}>
        {/* Global pages have their own headers, class pages handled by ClassWorkspace */}
        {['dashboard', 'settings', 'help', 'about', 'api-key-form', 'consent', 'class-management'].includes(selectedPage) && selectedPage !== 'dashboard' && (
          <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-semibold text-gray-800">
                  {selectedPage === 'class-management' ? 
                    `${(() => {
                      const hour = new Date().getHours();
                      if (hour < 12) return 'Good morning';
                      if (hour < 17) return 'Good afternoon';
                      return 'Good evening';
                    })()}, ${userName.firstName || username}!` :
                    selectedPage.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
                  }
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                {selectedPage !== 'class-management' && (
                  <>
                    <span className="text-sm text-gray-600">
                      Welcome, {userName.firstName} {userName.lastName}
                    </span>
                    {apiKey ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        API Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        No API Key
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className={`flex-1 ${
          selectedPage === 'about' ? 'overflow-y-auto p-6' :
          selectedPage === 'dashboard' ? 'overflow-hidden p-0' : 
          ['settings', 'help', 'api-key-form', 'consent'].includes(selectedPage) ? 'overflow-y-auto p-6' : 
          selectedPage === 'class-management' ? 'overflow-y-auto p-6' : 'overflow-hidden p-0'
        }`}>
          {renderCurrentPage()}
        </main>
      </div>
    </div>
  );
}

// Main App component with ClassProvider
function App() {
  return (
    <ClassProvider>
      <AppContent />
    </ClassProvider>
  );
}

export default App;